import AutocompleteElement from './AutocompleteElement';
import { useController, useWatch } from 'react-hook-form';
import { useFormError } from '@components/common/HookFormMui/FormErrorProvider';

/**
 * Same as the usual AutocompleteElement except this allows for text to be entered when "Other" is selected.
 *
 * @param param0
 * @param param0.control
 * @param param0.name - Name used for the dropdown field
 * @param param0.textFieldName - Name used for the "Other" input text field
 * @param param0.customRequired - Whether or not the custom text field is required when active
 * @returns {JSX.Element}
 * @constructor
 */
const AutocompleteOther = ({
  control,
  name,
  textFieldName,
  textFieldProps = {},
  autocompleteProps = {},
  rules,
  parseError,
  customRequired = true,
  ...rest
}) => {
  const lesson = useWatch({ control, name });
  const inCustomMode = !!lesson?.custom;

  const errorMsgFn = useFormError();
  const customErrorFn = parseError || errorMsgFn;

  const isSameOption = (a, b) => {
    if (!a || !b) return a === b;
    if (typeof autocompleteProps?.isOptionEqualToValue === 'function') {
      return autocompleteProps.isOptionEqualToValue(a, b);
    }
    return (a.id ?? a) === (b.id ?? b);
  };

  const validationRules = {
    ...rules,
    validate: {
      ...rules?.validate,
      customRequired: (value, formValues) => {
        if (!customRequired || !formValues?.[name]?.custom) return true;
        return !!String(value ?? '').trim() || (rules?.required || 'This field is required.');
      },
    },
  };

  const { field: customText, fieldState: { error } } = useController({
    name: textFieldName,
    control,
    rules: validationRules,
  });

  return (
    <AutocompleteElement
      name={name}
      control={control}
      {...rest}
      autocompleteProps={{
        selectOnFocus: !inCustomMode,
        clearOnBlur: !inCustomMode,
        ...autocompleteProps,
        filterOptions: inCustomMode ? (o) => o : undefined,  // typing isn't a filter here
        getOptionLabel: (option) =>
          option?.custom && !option?.customText && isSameOption(option, lesson) && customText.value
            ? `${option.label}: ${customText.value}`
            : (option?.label ?? ''),
        onChange: (event, newValue, reason, details) => {
          const changed = !isSameOption(newValue, lesson);
          if (newValue?.custom) {
            if (newValue.customText != null) {
              customText.onChange(newValue.customText);   // seeded row asserts its value
            } else if (changed) {
              customText.onChange('');                    // free-entry row clears on arrival
            }
          } else {
            customText.onChange(null);
          }
          autocompleteProps?.onChange?.(event, newValue, reason, details);
        },
      }}
      textFieldProps={{
        ...textFieldProps,
        ...(inCustomMode && {
          required: customRequired,
          placeholder: textFieldProps?.placeholder ?? 'Please describe',
          error: !!error,
          helperText: error ? typeof customErrorFn === 'function' ? customErrorFn(error) : error.message : textFieldProps?.helperText,
          inputProps: {
            ...textFieldProps?.inputProps,
            value: customText.value ?? '',
            onChange: (e) => customText.onChange(e.target.value),
          },
        }),
      }}
    />
  );
};

export default AutocompleteOther;
